import { useState, useCallback, useRef, useEffect } from 'react'
import { sendChatMessage, getMyThread, type AttachedFile, type ThreadMessage, type ConversationHandler, type ResolvedCustomer } from '@/lib/api'
import { trimHistory, HISTORY_PAIR_LIMIT, type ChatMessage } from '@/lib/chat-utils'
import type { ResponsePart } from '@/lib/render'

export { HISTORY_PAIR_LIMIT } from '@/lib/chat-utils'
export { DISPLAY_PAIR_LIMIT } from '@/lib/chat-utils'
export { trimHistory } from '@/lib/chat-utils'

export interface ChatEntry {
  id: string
  role: 'user' | 'assistant'
  content?: string
  parts?: ResponsePart[]
  error?: string
  timestamp: Date
  // Files attached to this user message (display only)
  attachedFileNames?: string[]
  // Conversation-mode metadata: who sent this (innoAI vs a named team member).
  senderLabel?: string
  senderType?: 'customer' | 'ai' | 'staff' | 'system' | 'automation'
}

interface UseChatOptions {
  // When true, the chat hydrates from and polls the persisted customer conversation
  // thread (so a human reply and AI reply both appear), and exposes the handler so
  // the UI can show "a team member is helping".
  customerThread?: boolean
  pollMs?: number
  // Optional first message shown when the chat is empty (e.g. today's calendar,
  // fetched statically via the API). Not sent to the LLM as history. Re-appears on
  // "new chat". Ignored in customerThread mode.
  introEntry?: ChatEntry | null
  // Called when a turn resolved exactly one customer (v1-chat's resolved_customer).
  // The operator surface uses it to move the composer's customer pin, so a search
  // done in chat and a pin done in the composer are one selection, not two.
  onCustomerResolved?: (customer: ResolvedCustomer) => void
}

// Maps persisted thread messages to UI entries (customer perspective).
// Exported for reuse by the customer history panel.
export function serverToEntries(msgs: ThreadMessage[]): ChatEntry[] {
  return msgs.map((m) => {
    if (m.sender_type === 'customer') {
      return {
        id: m.id,
        role: 'user' as const,
        content: m.body,
        timestamp: new Date(m.created_at),
        attachedFileNames: m.attachments?.length
          ? m.attachments.map((a) => a.name ?? (a.storage_path ? 'photo' : 'attachment'))
          : undefined,
      }
    }
    const parts: ResponsePart[] = (m.parts && m.parts.length)
      ? m.parts
      : (m.body ? [{ kind: 'text', content: m.body }] : [])
    return {
      id: m.id,
      role: 'assistant' as const,
      parts,
      senderLabel: m.sender_label,
      senderType: m.sender_type,
      timestamp: new Date(m.created_at),
    }
  })
}

function serverToHistory(msgs: ThreadMessage[]): ChatMessage[] {
  return msgs
    .filter((m) => m.sender_type !== 'system' && (m.body || m.parts?.length))
    .map((m) => ({
      role: m.sender_type === 'customer' ? ('user' as const) : ('assistant' as const),
      content: m.body || '[rendered content]',
    }))
}

export function useChat(options?: UseChatOptions) {
  const customerThread = options?.customerThread ?? false
  const pollMs = options?.pollMs ?? 4000
  const introEntry = options?.introEntry ?? null

  const [entries, setEntries] = useState<ChatEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [handler, setHandler] = useState<ConversationHandler | null>(null)
  // Held in a ref so a changing callback identity cannot re-create sendMessage —
  // ChatInterface's autoAsk effect keys off sendMessage, and a new identity there
  // would re-fire a handed-off question.
  const onCustomerResolvedRef = useRef(options?.onCustomerResolved)
  onCustomerResolvedRef.current = options?.onCustomerResolved
  // Full history kept in memory; trimmed to HISTORY_PAIR_LIMIT before each API call
  const historyRef = useRef<ChatMessage[]>([])
  const loadingRef = useRef(false)

  // Seed the intro message (operator mode) once it's ready, as long as the operator
  // hasn't started chatting yet. Not pushed to historyRef — it's display-only.
  const introSeededRef = useRef(false)
  useEffect(() => {
    if (customerThread || !introEntry || introSeededRef.current) return
    introSeededRef.current = true
    setEntries(prev => (prev.length === 0 ? [introEntry] : prev))
  }, [customerThread, introEntry])

  // Pull the authoritative thread from the server (customer mode only).
  const refreshThread = useCallback(async () => {
    if (!customerThread) return
    try {
      const res = await getMyThread()
      setHandler(res.conversation?.handler ?? 'ai')
      if (res.messages.length) {
        setEntries(serverToEntries(res.messages))
        historyRef.current = serverToHistory(res.messages)
      }
    } catch {
      // transient — keep current state, next poll retries
    }
  }, [customerThread])

  // Hydrate on mount + poll for replies (customer mode).
  useEffect(() => {
    if (!customerThread) return
    refreshThread()
    const timer = setInterval(() => {
      if (!loadingRef.current) refreshThread()
    }, pollMs)
    return () => clearInterval(timer)
  }, [customerThread, pollMs, refreshThread])

  const sendMessage = useCallback(async (text: string, files?: AttachedFile[], noteStyleId?: string, noteCustomerId?: string) => {
    if (!text.trim() || isLoading) return

    const userEntry: ChatEntry = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date(),
      attachedFileNames: files?.length ? files.map((f) => f.name) : undefined,
    }

    setEntries(prev => [...prev, userEntry])
    historyRef.current.push({ role: 'user', content: text.trim() })
    setIsLoading(true)
    loadingRef.current = true

    try {
      const trimmed = trimHistory(historyRef.current, HISTORY_PAIR_LIMIT)
      const response = await sendChatMessage(trimmed, files, noteStyleId, noteCustomerId)

      const assistantText = response.parts
        .filter(p => p.kind === 'text')
        .map(p => (p as { kind: 'text'; content: string }).content)
        .join('\n')

      // Render-only turns must still leave a textual trace: history round-trips
      // as text, so a bare '[rendered content]' erases whatever the component
      // showed (e.g. which customers a customer_confirm offered) from every
      // later turn. Captions carry the gist.
      const renderTrace = response.parts
        .filter(p => p.kind === 'render')
        .map(p => {
          const r = p as { component: string; caption?: string }
          return `[rendered ${r.component}${r.caption ? `: ${r.caption}` : ''}]`
        })
        .join('\n')

      historyRef.current.push({
        role: 'assistant',
        content: assistantText || renderTrace || '[rendered content]',
      })

      const assistantEntry: ChatEntry = {
        id: crypto.randomUUID(),
        role: 'assistant',
        parts: response.parts,
        senderLabel: customerThread ? 'innoAI' : undefined,
        senderType: customerThread ? 'ai' : undefined,
        timestamp: new Date(),
      }

      setEntries(prev => [...prev, assistantEntry])

      // Announced after the reply so the pin change reads as a consequence of it.
      if (response.resolved_customer?.id) onCustomerResolvedRef.current?.(response.resolved_customer)

      // Reconcile with the persisted thread (picks up the authoritative ids, any
      // human handoff state, and avoids drift).
      if (customerThread) {
        loadingRef.current = false
        await refreshThread()
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      const errorEntry: ChatEntry = {
        id: crypto.randomUUID(),
        role: 'assistant',
        parts: [{ kind: 'text', content: `Error: ${message}` }],
        error: message,
        timestamp: new Date(),
      }
      setEntries(prev => [...prev, errorEntry])
    } finally {
      setIsLoading(false)
      loadingRef.current = false
    }
  }, [isLoading, customerThread, refreshThread])

  // A display-only line in the thread (used for "notes now about X"). Deliberately
  // NOT pushed to historyRef — it is feedback to the operator, not something the
  // assistant said. The pin itself reaches the model as system context on every
  // send, so nothing is lost by keeping it out of the transcript.
  const addSystemNote = useCallback((text: string) => {
    setEntries(prev => [...prev, {
      id: crypto.randomUUID(),
      role: 'assistant',
      parts: [{ kind: 'text', content: text }],
      senderType: 'system',
      timestamp: new Date(),
    }])
  }, [])

  const clearHistory = useCallback(() => {
    setEntries(introEntry ? [introEntry] : [])
    historyRef.current = []
  }, [introEntry])

  return {
    entries,
    isLoading,
    handler,
    sendMessage,
    addSystemNote,
    clearHistory,
  }
}
